import { Controller } from '@hotwired/stimulus';

// Asks Hey Amba a prompt by doing what a user does by hand: open the drawer,
// switch to the AI chat tab, start a new conversation, type the prompt and send.
// So the only request that reaches the server is the ordinary composer submit,
// and this consumes existing hooks (#amba-drawer-toggle, #ai-chat-tab, the frame
// and composer ids from AiChatShellComponent) without touching the shared
// drawer / ai_chat / ai_chat_form controllers.
export default class extends Controller {
  static values = {
    autoOpenParam: String,
    composerId: String,
    fallbackUrl: String,
    frameId: String,
    newChatUrl: String,
    prompt: String,
  };

  // autoOpenParam means a prompt was handed over in the url (see #open's
  // fallback). Strip it first, so a refresh cannot resend the prompt.
  connect() {
    if (!this.hasAutoOpenParamValue) return;

    this.#stripAutoOpenParam();
    this.open();
  }

  // The trigger is an href="#" link, so an unprevented click appends "#" to the
  // url and buries the previous page one Back press deeper.
  open(event) {
    event?.preventDefault();

    // Only the VIP profile renders the chat frame, so from anywhere else the
    // prompt travels there in a query param rather than failing silently.
    if (this.#frameMissing()) {
      window.Turbo.visit(this.fallbackUrlValue);
      return;
    }

    this.openDrawer();
    this.switchToAiTab();
    this.startNewChatThenSubmit();
    this.dismissModal();
  }

  // Every prompt starts its own conversation rather than landing in whatever
  // thread the user left open, and the composer it types into is the one the
  // new-chat response renders — hence the wait for turbo:frame-load.
  startNewChatThenSubmit() {
    if (!this.hasFrameIdValue || !this.hasNewChatUrlValue) {
      this.submitPrompt();
      return;
    }

    const frame = document.getElementById(this.frameIdValue);
    if (!frame) {
      console.warn(`Turbo frame with ID "${this.frameIdValue}" not found`);
      this.submitPrompt();
      return;
    }

    // One outcome only: a listener left on this persistent frame would submit the
    // prompt into whatever it renders NEXT, so failures must remove it too.
    const outcome = new AbortController();
    frame.addEventListener(
      'turbo:frame-load',
      () => {
        outcome.abort();
        this.submitPrompt();
      },
      { signal: outcome.signal }
    );
    for (const failure of [
      'turbo:fetch-request-error',
      'turbo:frame-missing',
    ]) {
      frame.addEventListener(
        failure,
        () => {
          outcome.abort();
          console.warn(`New chat failed to load (${failure}); prompt not sent`);
        },
        { signal: outcome.signal }
      );
    }
    // Assigning an identical src is a Turbo no-op — no fetch, no
    // turbo:frame-load — so an already-open new chat needs reload() instead.
    if (
      frame.src === new URL(this.newChatUrlValue, window.location.href).href
    ) {
      frame.reload();
    } else {
      frame.src = this.newChatUrlValue;
    }
  }

  // A busy composer (ai_chat_form disables submit while a response streams) keeps
  // the prompt in the textarea for the user to send: never queued, never dropped.
  submitPrompt() {
    const composer = document.getElementById(this.composerIdValue);
    if (!composer) {
      console.warn(
        `Composer element with ID "${this.composerIdValue}" not found`
      );
      return;
    }

    const textarea = composer.querySelector('textarea');
    const form = composer.querySelector('form');
    if (!textarea || !form) {
      console.warn(`Composer "${this.composerIdValue}" has no form/textarea`);
      return;
    }

    textarea.value = this.promptValue;
    textarea.focus();
    // Let ai_chat_form (and anything else listening) see the change.
    textarea.dispatchEvent(new Event('input', { bubbles: true }));

    const submit = form.querySelector('[data-ai-chat-form-target="submit"]');
    if (submit?.disabled) return;

    form.requestSubmit();
  }

  // The trigger submits nothing, so the modal's usual
  // `turbo:submit-success->remote-modal#closeModal` never fires and the backdrop
  // would be left behind. Hiding it through the controller runs its
  // `hidden.bs.modal` teardown. No-ops outside a modal.
  dismissModal() {
    const modalEl = document.querySelector('[data-controller~="remote-modal"]');
    if (!modalEl) return;

    const controller = this.application.getControllerForElementAndIdentifier(
      modalEl,
      'remote-modal'
    );
    // Pass a benign event so closeModal's `event.type` guard is a no-op (it only
    // forwards for turbo:submit-success).
    controller?.closeModal(new Event('hey-amba-prompt:dismiss'));
  }

  #frameMissing() {
    if (!this.hasFallbackUrlValue || !this.hasFrameIdValue) return false;

    return !document.getElementById(this.frameIdValue);
  }

  #stripAutoOpenParam() {
    const url = new URL(window.location.href);
    url.searchParams.delete(this.autoOpenParamValue);
    window.history.replaceState(window.history.state, '', url.toString());
  }

  openDrawer() {
    const toggle = document.getElementById('amba-drawer-toggle');
    if (!toggle || toggle.checked) return;

    toggle.checked = true;
    // Mirror clicking the drawer <label>: fire change so drawer_controller
    // persists the open state to localStorage.
    toggle.dispatchEvent(new Event('change'));
  }

  switchToAiTab() {
    // Present only when the drawer has both notes + AI tabs; AI-only drawers show
    // the chat directly, so the optional chain is a no-op there.
    document.getElementById('ai-chat-tab')?.click();
  }
}
